import {
    useCallback,
    useMemo,
} from 'react'

import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'

import usePersistedExtendedState from './hooks/usePersistedExtendedState'
import usePersistedResize from './hooks/usePersistentResize'
import useStyles from './ReorderableAccordionItem.styles'

export type AccordionItemType = {
    title: string,
    content?: React.JSX.Element,
    renderContent?: (currentHeight: number) => React.JSX.Element,
    key: string,
}

const MinHeightPx = 200
const MaxHeightPx = 600

type ReorderableAccordionItemProps = {
    item: AccordionItemType,
    moveItem: (index: number, direction: 'up' | 'down') => void,
    index: number,
    first: boolean,
    last: boolean,
    initialHeightVh?: number,
}

function vhToPixels(vh: number) {
    const oneVhInPx = window.innerHeight / 100

    return oneVhInPx * vh
}

export default function ReorderableAccordionItem({
    item,
    moveItem,
    index,
    first,
    last,
    initialHeightVh,
}: Readonly<ReorderableAccordionItemProps>) {
    const {
        classes,
        cx,
    } = useStyles()
    const {
        title,
        key,
        renderContent,
        content,
    } = item

    const {
        containerRef,
        currentHeightPx,
    } = usePersistedResize({
        persistentKey: key,
        initialHeightPx: vhToPixels(initialHeightVh),
    })

    const contentChild = useMemo(() => {
        return renderContent ? renderContent(currentHeightPx) : content
    }, [
        content,
        renderContent,
        currentHeightPx,
    ])

    const [
        expanded,
        setExpanded,
    ] = usePersistedExtendedState({
        persistentKey: key,
    })

    const onExpandChange = useCallback((event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded)
    }, [setExpanded])

    return (
        <div className={classes.accordionContainer}>
            <Accordion
                key={key}
                className={classes.accordion}
                onChange={onExpandChange}
                expanded={expanded}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    className={classes.accordionSummary}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div className={classes.headerTitle}>{title}</div>
                        <Stack direction="row">
                            <IconButton
                                size="small"
                                component="span"
                                role="button"
                                className={cx(first && classes.disabledButton)}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (first) return
                                    moveItem(index, 'up')
                                }}
                            >
                                <ArrowUpward fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                component="span"
                                role="button"
                                className={cx(last && classes.disabledButton)}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (last) return
                                    moveItem(index, 'down')
                                }}
                            >
                                <ArrowDownward fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{
                    p: 0,
                }}
                >
                    <div
                        ref={containerRef}
                        style={{
                            height: `${currentHeightPx}px`,
                            minHeight: `${MinHeightPx}px`,
                            maxHeight: `${MaxHeightPx}px`,
                        }}
                        className={classes.resizeWrapper}
                    >
                        {contentChild}
                    </div>
                </AccordionDetails>
            </Accordion>
        </div>
    )
}
